import React from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { captureRefFromUrl } from "@/lib/refCode";
import { isNative } from "@/lib/native";
import { Toaster } from "@/components/ui/sonner";
import AuthedHolidayPopup from "@/components/AuthedHolidayPopup";
import { PackJobsProvider } from "@/components/social/usePackJobs";
import PackJobsTray from "@/components/social/PackJobsTray";
import { BgJobsProvider } from "@/components/jobs/useBgJobs";
import BgJobsTray from "@/components/jobs/BgJobsTray";
import { BatchEnhanceTrackerProvider } from "@/context/BatchEnhanceTracker";
import BatchTrayPill from "@/components/photo/BatchTrayPill";
import BatchCompletionToaster from "@/components/photo/BatchCompletionToaster";
import ErrorBoundary from "@/components/ErrorBoundary";
import NativeBootstrap from "@/components/NativeBootstrap";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import Contatti from "@/pages/Contatti";
import Programmati from "@/pages/Programmati";
import NewProperty from "@/pages/NewProperty";
import VoiceWelcome from "@/pages/VoiceWelcome";  // iter v29 r080 · voice-first onboarding
import PropertyDetail from "@/pages/PropertyDetail";
import Pricing from "@/pages/Pricing";
import UpgradeIOS from "@/pages/UpgradeIOS";  // iter v30 r145 · paywall Apple IAP
import BillingSuccess from "@/pages/BillingSuccess";
import VerifyEmail from "@/pages/VerifyEmail";
import MockupRecorder from "@/pages/MockupRecorder";  // iter v28 r466 · Playwright-only
import ReelRecorder from "@/pages/ReelRecorder";  // iter v29 r001 · 8 reel servizio
import AppStoreShotSocial from "@/pages/promo/AppStoreShotSocial";  // iter r96g · shot App Store Connect
import ReelProntoPreview from "@/pages/promo/ReelProntoPreview";  // iter r97c · reel Instagram 20s
import ReelHeroMockup from "@/pages/promo/ReelHeroMockup";  // iter r99j · reel hero landing 32s
import ReelEmotional from "@/pages/ReelEmotional";  // iter v29 r048 · reel prima/dopo
import PublicListing from "@/pages/PublicListing";
import Profile from "@/pages/Profile";
import Demo from "@/pages/Demo";
import VolantinoStimaAnteprima from "@/pages/VolantinoStimaAnteprima";
// iter v29 r187 — RIMOSSO import AnteprimaSocialPronto (rotta /anteprima/social
// rimossa, era una pagina personale per i contenuti Instagram non parte dell'app).
// import AnteprimaSocialPronto from "@/pages/AnteprimaSocialPronto";
import AnteprimaVetrina from "@/pages/AnteprimaVetrina";
import InstagramAppDownload from "@/pages/share/InstagramAppDownload";
import InstagramAppDownloadReel from "@/pages/share/InstagramAppDownloadReel";  // iter v32 r48 · User M1444 · reel 9:16 nativo
import StoryVetrinaAgente from "@/pages/share/StoryVetrinaAgente";  // iter v32 r48 · User M1452 · Story IG vetrina
import InstagramCarousel from "@/pages/share/InstagramCarousel";
import InstagramLancioVetrina from "@/pages/share/InstagramLancioVetrina";  // iter v32 r37v · User M1454
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";  // iter v29 r249 · privacy standalone per App Store
import Supporto from "@/pages/Supporto";  // iter v29 r249 · supporto standalone per App Store
import PublicContact from "@/pages/PublicContact";  // iter v29 r261 · form contatti pubblico anti-scraping
import ExtensionTerms from "@/pages/ExtensionTerms";
import PrivacyExtension from "@/pages/PrivacyExtension";
import Estensione from "@/pages/Estensione";
import NativeOAuthBridge from "@/pages/NativeOAuthBridge";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Analytics from "@/pages/Analytics";
import Referrals from "@/pages/Referrals";
import Admin from "@/pages/Admin";
import AdminPulse from "@/pages/AdminPulse";  // iter v29 r231 · console admin essenziale
import Aiuto from "@/pages/Aiuto";  // iter v29 r233 · centro guide a prova di stupido
import LlmBudget from "@/pages/LlmBudget";  // iter v29 r004 · pannello consumo Universal Key
import OgShowcase from "@/pages/OgShowcase";
import ExtensionPage from "@/pages/ExtensionPage";
import LivePreview from "@/pages/LivePreview";
import FotoEditor from "@/pages/FotoEditor";
import ReelAutoMockup from "@/pages/dashboard/ReelAutoMockup";
import SocialStudio from "@/pages/SocialStudio";
import SocialShare from "@/pages/SocialShare";
import DriveMode from "@/pages/DriveMode";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthMagic from "@/pages/AuthMagic";  // iter v30 r140 · magic link login
import Portali from "@/pages/Portali";
import EstimateValue from "@/pages/EstimateValue";
import Sopralluogo from "@/pages/Sopralluogo";
// [iter v28 r318] sezione "Acquisizione" rimossa su richiesta utente
import Assistente from "@/pages/Assistente";
import GalleriaFoto from "@/pages/GalleriaFoto";
import Crediti from "@/pages/Crediti";
import MyContent from "@/pages/MyContent";
import Wrapped from "@/pages/Wrapped";
import Agenda from "@/pages/Agenda";
import Idee from "@/pages/Idee";
import Stima from "@/pages/Stima";
import Agenzia from "@/pages/Agenzia";
import AgentMandatoLanding from "@/pages/AgentMandatoLanding";
import LegacySlugRedirect from "@/pages/LegacySlugRedirect";
import Trattative from "@/pages/Trattative";
import VendiCasa from "@/pages/VendiCasa";
import MieProposte from "@/pages/MieProposte";
import Marketplace from "@/pages/Marketplace";
import PubblicaVeloce from "@/pages/PubblicaVeloce";
import RadarComingSoon from "@/pages/RadarComingSoon";
import MarketplaceComingSoon from "@/pages/MarketplaceComingSoon";
import MandatiComingSoon from "@/pages/MandatiComingSoon";
import NetworkComingSoon from "@/pages/NetworkComingSoon";
import CinematicPreview from "@/pages/CinematicPreview";
import AppMobileComingSoon from "@/pages/AppMobileComingSoon";
import ComeFunzionaPage from "@/pages/ComeFunzionaPage";
import AILeadAutoreply from "@/pages/AILeadAutoreply";
import GuidaInstagram from "@/pages/GuidaInstagram";
import ImportGestionale from "@/pages/ImportGestionale";
import QuantoValeCasaMia from "@/pages/QuantoValeCasaMia";
import QuantoValeCasaMiaCity from "@/pages/QuantoValeCasaMiaCity";
import RichiesteStima from "@/pages/RichiesteStima";
import StimaComparativaBuilder from "@/pages/StimaComparativaBuilder";
import MiaLanding from "@/pages/MiaLanding";
import MieRecensioni from "@/pages/MieRecensioni";
import PublicReview from "@/pages/PublicReview";
import AgentPublicProfile from "@/pages/AgentPublicProfile";  // iter v32 r37 · /agente/:slug
import VetrinaAgente from "@/pages/VetrinaAgente";  // iter v32 r37 · config pagina pubblica (PRO)
import CookieBanner from "@/components/CookieBanner";
import GlobalMicFab from "@/components/GlobalMicFab";
import CreditExhaustedModal from "@/components/CreditExhaustedModal";
import RateLimitToastListener from "@/components/RateLimitToastListener";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import OnboardingTour, { hasSeenOnboarding } from "@/components/OnboardingTour";

/**
 * NativeRootRedirect — iter v29 r182 / r183
 * Quando l'app gira dentro Capacitor iOS (TestFlight/App Store), apre
 * "/" — ma un agente che ha installato l'app NON vuole vedere la landing
 * marketing rivolta a nuovi visitatori. Vuole andare direttamente al
 * suo lavoro: dashboard se loggato, login altrimenti.
 *
 * r183 aggiunto onboarding tour 5-slide al primo avvio (una volta sola
 * grazie a localStorage 'pronto_onboarding_seen').
 *
 * Su web la "/" continua a mostrare <Landing /> normalmente.
 */
function NativeRootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#7C8B6E] flex items-center justify-center" data-testid="native-root-loading">
        <img src="/logo/mark-symbol.png" alt="prontoimmo.it" width="64" height="64" style={{ objectFit: "contain", opacity: 0.9 }} draggable="false" />
      </div>
    );
  }
  // Se loggato → vai diretto a dashboard (skip onboarding anche se mai visto:
  // un utente loggato è ovviamente già esperto)
  if (user) return <Navigate to="/dashboard" replace />;
  // Non loggato + onboarding non visto → mostra tour, poi login
  if (!hasSeenOnboarding()) return <OnboardingTour />;
  // Non loggato + onboarding già visto → login diretto
  return <Navigate to="/login" replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // iter v28 r431 — User M1343 bugfix: salviamo il path corrente prima del
  // redirect a /login così dopo l'autenticazione l'utente torna ESATTAMENTE
  // dove stava cercando di andare (es. /dashboard/guida-instagram). Prima
  // il login redirezionava sempre alla home /dashboard generica → l'utente
  // non capiva dove fosse la pagina che cercava.
  // redirect a /login così dopo l'autenticazione l'utente torna ESATTAMENTE
  // dove stava cercando di andare (es. /dashboard/guida-instagram). Prima
  // il login redirezionava sempre alla home /dashboard generica → l'utente
  // non capiva dove fosse la pagina che cercava.
  React.useEffect(() => {
    if (!loading && !user) {
      try {
        const target = location.pathname + (location.search || "") + (location.hash || "");
        // Solo path interni "veri", no /login per evitare loop
        if (target && target !== "/login" && /^\/[^/]/.test(target)) {
          sessionStorage.setItem("post_login_redirect", target);
        }
      } catch { /* no-op */ }
    }
  }, [user, loading, location.pathname, location.search, location.hash]);
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F4F0]" data-testid="loading-screen">
        {/* Top bar shimmer — feels instantaneous, no centered splash */}
        <div className="h-14 border-b border-[#EAE7DF] bg-white flex items-center px-4 sm:px-6">
          {/* iter v28 r97 — font-brand (Montserrat, font del logo) invece di
              font-editorial (Manrope). Più coerente col brand. */}
          <span className="inline-flex items-center gap-2 vista-pulse">
            <img src="/logo/mark-symbol.png" alt="Pronto" width="22" height="22" style={{ objectFit: "contain" }} draggable="false" />
            <span className="font-brand text-[16px] tracking-tight text-[#7C8B6E]">Pronto</span>
          </span>
        </div>
        {/* Content skeleton */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          <div className="h-6 w-40 rounded bg-[#EAE7DF]/70 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-white border border-[#EAE7DF] animate-pulse" />
            ))}
          </div>
          <div className="h-32 rounded-xl bg-white border border-[#EAE7DF] animate-pulse" />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // iter 313 r36 — Guard "wrong domain": il proxy Emergent a volte serve
  // l'app da un sotto-dominio cache `*.preview.static.emergentagent.com`
  // dove le API backend NON esistono. Risultato: Google OAuth callback
  // fallisce con "Accesso Google fallito" perché /api/auth/session è 404.
  // Se rileviamo il dominio static, redirigiamo SUBITO al dominio principale
  // (REACT_APP_BACKEND_URL) preservando path + hash + search.
  React.useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const host = window.location.hostname;
      if (host.includes(".preview.static.")) {
        const targetOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        if (targetOrigin && !targetOrigin.includes(".static.")) {
          const newUrl = targetOrigin
            + window.location.pathname
            + window.location.search
            + window.location.hash;
          window.location.replace(newUrl);
        }
      }
    } catch { /* no-op */ }
  }, []);

  // Capture ?ref= from any URL (referral link from another agent)
  React.useEffect(() => {
    captureRefFromUrl();
  }, [location.search]);

  // iter v29 r191 — Universal Link listener (iOS native).
  // Quando l'utente completa il login Google nella SFSafariViewController
  // esterna, Safari fa redirect a https://prontoimmo.it/dashboard#session_id=XXX.
  // iOS riconosce il dominio (associated domains in App.entitlements) e
  // apre l'app passando l'URL via il listener appUrlOpen. Qui chiudiamo
  // la Safari View e navighiamo a /dashboard preservando l'hash così
  // AuthCallback può scambiare il session_id come fa sul web.
  React.useEffect(() => {
    if (!isNative) return;
    let unsubscribe = () => {};
    (async () => {
      const { nativeOnDeepLink, nativeCloseBrowser } = await import("@/lib/native");
      unsubscribe = await nativeOnDeepLink((url) => {
        try {
          // Chiudi la Safari View modale
          nativeCloseBrowser();
          // iter v29 r195 — Gestisci sia Universal Links (https://prontoimmo.it/...)
          // che custom URL scheme (it.prontoimmo.app://oauth-complete#session_id=XXX).
          // Per il custom scheme, ricostruisci il path target /dashboard
          // preservando il fragment con session_id.
          let target;
          if (url.startsWith("it.prontoimmo.app://oauth-complete")) {
            // Estrai search + hash dal custom URL (ignora "oauth-complete" path)
            const idx = url.indexOf("?");
            const hashIdx = url.indexOf("#");
            const search = idx >= 0 ? url.slice(idx, hashIdx >= 0 ? hashIdx : undefined) : "";
            const hash = hashIdx >= 0 ? url.slice(hashIdx) : "";
            target = `/dashboard${search}${hash}`;
          } else if (url.startsWith("it.prontoimmo.app://")) {
            // Altri custom path → naviga al path estratto
            const cleaned = url.replace("it.prontoimmo.app://", "/");
            target = cleaned;
          } else {
            // Universal Link HTTPS — estrai path completo
            const u = new URL(url);
            target = u.pathname + u.search + u.hash;
          }
          if (target && target !== window.location.pathname + window.location.search + window.location.hash) {
            navigate(target, { replace: true });
          }
        } catch (_) { /* malformed URL — ignore */ }
      });
    })();
    return () => { try { unsubscribe(); } catch (_) { /* no-op */ } };
  }, [navigate]);

  // Scroll to top on route change (avoid landing in middle of next page).
  // Skip on hash links (#agenda, #faq) so anchor scroll still works.
  React.useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* iter v29 r182 — App nativa iOS: la "/" mostra DIRETTAMENTE
          la dashboard se loggato, altrimenti il login.
          Sul web, "/" mostra la landing marketing normale.
          Un agente che apre l'app installata non vuole vedere la pagina
          di marketing rivolta a nuovi visitatori. */}
      <Route path="/" element={
        isNative ? <NativeRootRedirect /> : <Landing />
      } />
      <Route path="/prova" element={<Demo />} />
      <Route path="/anteprima/volantino-stima" element={<VolantinoStimaAnteprima />} />
      {/* iter v29 r187 — RIMOSSA route /anteprima/social (Calendario Infame).
          Era una pagina personale per i contenuti Instagram, non una feature
          dell'app. Il file pages/AnteprimaSocialPronto.jsx resta come bozza
          ma non è più raggiungibile da nessun link/menu/URL diretto. */}
      <Route path="/anteprima/vetrina/:style" element={<AnteprimaVetrina />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/supporto" element={<Supporto />} />
      <Route path="/contatti" element={<PublicContact />} />
      <Route path="/contattaci" element={<PublicContact />} />
      <Route path="/extension/terms" element={<ExtensionTerms />} />
      <Route path="/privacy-extension" element={<PrivacyExtension />} />
      <Route path="/estensione" element={<Estensione />} />
      {/* iter v29 r195 — Pagina bridge per OAuth Google nativo iOS */}
      <Route path="/native-oauth-bridge" element={<NativeOAuthBridge />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* iter v30 r140 — Magic link: l'utente apre il link ricevuto via email
          e atterra qui. Su iOS native è raggiunto anche via Universal Link
          (apple-app-site-association → components: /auth/*). */}
      <Route path="/auth/magic" element={<AuthMagic />} />
      {/* iter v30 r145 — Apple 3.1.1: /pricing su iOS native ora redirige
          al paywall IAP Apple (era nascosto in v30 r144). Su web resta lo
          Stripe checkout classico. */}
      <Route path="/pricing" element={isNative ? <Navigate to="/dashboard/upgrade" replace /> : <Pricing />} />
      <Route path="/dashboard/upgrade" element={<UpgradeIOS />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      {/* iter v28 r466 — Pagina interna usata da Playwright per registrare
          MP4 dei mockup landing (social-kit). NON linkata, NON SEO. */}
      <Route path="/mockup-recorder" element={<MockupRecorder />} />
      <Route path="/reel-recorder" element={<ReelRecorder />} />
      {/* iter r96g · User M1517 — Shot dedicato per App Store Connect (contenuti social). */}
      <Route path="/promo/app-store-shot-social" element={<AppStoreShotSocial />} />
      {/* iter r97c · User M1517 — Reel Instagram/App Preview 1080x1920 autoplay 20s. */}
      <Route path="/promo/reel-pronto-preview" element={<ReelProntoPreview />} />
      {/* iter r99j · User M1523 — Reel hero mockup landing 32s animazione loop. */}
      <Route path="/promo/reel-hero-mockup" element={<ReelHeroMockup />} />
      <Route path="/reel-emotional" element={<ReelEmotional />} />
      <Route path="/listing/:publicId" element={<PublicListing />} />
      {/* iter v28 r440 — Anteprima pre-pubblicazione owner-only. Stessa
          UI di /listing/:publicId ma il fetch è autenticato. */}
      <Route path="/listing-preview/:publicId" element={<PublicListing />} />
      <Route path="/og/showcase" element={<OgShowcase />} />
      <Route path="/come-funziona" element={<ComeFunzionaPage />} />
      <Route path="/r/:shareId" element={<SocialShare />} />
      <Route path="/stima/:code" element={<Stima />} />
      {/* iter v32 r37 — Vetrina Agente: pagina pubblica `/agente/:slug` con
          portfolio immobili in 4 layout. Riservata a utenti PRO (il backend
          restituisce 404 se l'agente non è più Pro). */}
      <Route path="/agente/:slug" element={<AgentPublicProfile />} />
      {/* iter v32 r37g · Post Instagram 1080×1350 "Scarica l'app" — pagina
          pubblica usata come canvas per generare l'asset PNG via screenshot. */}
      <Route path="/share/scarica-app" element={<InstagramAppDownload />} />
      <Route path="/share/scarica-app-reel" element={<InstagramAppDownloadReel />} />
      <Route path="/share/story-vetrina" element={<StoryVetrinaAgente />} />
      <Route path="/share/carosello-lancio" element={<InstagramCarousel />} />
      <Route path="/share/lancio-vetrina" element={<InstagramLancioVetrina />} />
      {/* iter v28 r367 — User M1277: la vecchia landing magazine /p/:slug
          ora redirige al funnel stima /stima/:code (single source of truth).
          Il componente fa una chiamata GET /api/public/agent/{slug}/code per
          ottenere il referral_code, poi Navigate. */}
      <Route path="/p/:slug" element={<LegacySlugRedirect />} />
      <Route path="/vendi-casa" element={<VendiCasa />} />
      <Route path="/vendi-casa/proposte/:token" element={<MieProposte />} />
      <Route path="/quanto-vale-casa-mia" element={<QuantoValeCasaMia />} />
      <Route path="/quanto-vale-casa-mia/:citta" element={<QuantoValeCasaMiaCity />} />
      {/* iter v32 r25 — Alias rotte più corte: per share-link in WhatsApp,
          SMS, biglietti da visita. Senza questi un copy-paste con il nome
          breve dava 404. */}
      <Route path="/quanto-vale-casa" element={<Navigate to="/quanto-vale-casa-mia" replace />} />
      <Route path="/stima" element={<Navigate to="/quanto-vale-casa-mia" replace />} />
      <Route path="/recensione/:token" element={<PublicReview />} />
      <Route
        path="/dashboard/marketplace"
        element={
          <ProtectedRoute>
            <Marketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/idee"
        element={
          <ProtectedRoute>
            <Idee />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/referrals"
        element={
          <ProtectedRoute>
            <Referrals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/guida-instagram"
        element={
          <ProtectedRoute>
            <GuidaInstagram />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin"
        element={
          <ProtectedRoute>
            <AdminPulse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/avanzato"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/budget"
        element={
          <ProtectedRoute>
            <LlmBudget />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/aiuto"
        element={
          <ProtectedRoute>
            <Aiuto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/live"
        element={
          <ProtectedRoute>
            <LivePreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/foto"
        element={
          <ProtectedRoute>
            <FotoEditor />
          </ProtectedRoute>
        }
      />
      {/* iter v29 r228 — User: «togli il Reel Auto-Editor per ora,
          dobbiamo implementarlo meglio». Rotta nascosta dal menu ma
          codice preservato in attesa di rifinitura. Chi arriva qui via
          URL diretto viene reindirizzato al dashboard. */}
      <Route
        path="/dashboard/reel-auto"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/dashboard/wrapped"
        element={
          <ProtectedRoute>
            <Wrapped />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/wrapped/:year"
        element={
          <ProtectedRoute>
            <Wrapped />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/social"
        element={
          <ProtectedRoute>
            <SocialStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/galleria"
        element={
          <ProtectedRoute>
            <GalleriaFoto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/contenuti"
        element={
          <ProtectedRoute>
            <MyContent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/crediti"
        element={
          <ProtectedRoute>
            <Crediti />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/portali"
        element={
          <ProtectedRoute>
            <Portali />
          </ProtectedRoute>
        }
      />
      {/* iter v29 r216 — Pubblica veloce: schermata mobile per copia-incolla
          dei campi annuncio nei moduli Idealista/Immobiliare.it */}
      <Route
        path="/dashboard/listings/:id/pubblica-veloce"
        element={
          <ProtectedRoute>
            <PubblicaVeloce />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/stima"
        element={
          <ProtectedRoute>
            <EstimateValue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/sopralluogo"
        element={
          <ProtectedRoute>
            <Sopralluogo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/assistente"
        element={
          <ProtectedRoute>
            <Assistente />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/agenda"
        element={
          <ProtectedRoute>
            <Agenda />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/agenzia"
        element={
          <ProtectedRoute>
            <Agenzia />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/extension"
        element={
          <ProtectedRoute>
            <ExtensionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      {/* iter v32 r37 — Configurazione della Vetrina Agente pubblica (PRO).
          Pagina dedicata con paywall integrato se l'utente non è Pro. */}
      <Route
        path="/dashboard/vetrina-agente"
        element={
          <ProtectedRoute>
            <VetrinaAgente />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <BillingSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="/cinematic-preview" element={<CinematicPreview />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      {/* iter v29 r080 — Voice-first onboarding per nuovi agenti */}
      <Route
        path="/dashboard/welcome"
        element={
          <ProtectedRoute>
            <VoiceWelcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/annunci"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      {/* iter v32 r25 — Alias rotta inglese per deep-link generici. */}
      <Route path="/dashboard/properties" element={<Navigate to="/dashboard/annunci" replace />} />
      <Route path="/dashboard/property" element={<Navigate to="/dashboard/annunci" replace />} />
      <Route
        path="/dashboard/import-gestionale"
        element={
          <ProtectedRoute>
            <ImportGestionale />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/radar"
        element={
          <ProtectedRoute>
            <RadarComingSoon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/futuro/app-mobile"
        element={
          <ProtectedRoute>
            <AppMobileComingSoon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/futuro/marketplace"
        element={
          <ProtectedRoute>
            <MarketplaceComingSoon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/futuro/network"
        element={
          <ProtectedRoute>
            <NetworkComingSoon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/futuro/mandati"
        element={
          <ProtectedRoute>
            <MandatiComingSoon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/futuro/risposta-ai"
        element={
          <ProtectedRoute>
            <AILeadAutoreply />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/richieste-stima"
        element={
          <ProtectedRoute>
            <RichiesteStima />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/stima-comparativa"
        element={
          <ProtectedRoute>
            <StimaComparativaBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/landing"
        element={
          <ProtectedRoute>
            <MiaLanding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/recensioni"
        element={
          <ProtectedRoute>
            <MieRecensioni />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/contatti"
        element={
          <ProtectedRoute>
            <Contatti />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/programmati"
        element={
          <ProtectedRoute>
            <Programmati />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/trattative"
        element={
          <ProtectedRoute>
            <Trattative />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/new"
        element={
          <ProtectedRoute>
            <NewProperty />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:id"
        element={
          <ProtectedRoute>
            <PropertyDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/drive"
        element={
          <ProtectedRoute>
            <DriveMode />
          </ProtectedRoute>
        }
      />
      <Route path="/agenda" element={<Navigate to="/dashboard/agenda" replace />} />
      <Route path="/profilo" element={<Navigate to="/dashboard/profile" replace />} />
      <Route path="/dashboard/profilo" element={<Navigate to="/dashboard/profile" replace />} />
      <Route path="/portali" element={<Navigate to="/dashboard/portali" replace />} />
      <Route path="/extension" element={<Navigate to="/dashboard/extension" replace />} />
      <Route path="/stima" element={<Navigate to="/dashboard/stima" replace />} />
      <Route path="/idee" element={<Navigate to="/dashboard/idee" replace />} />
      <Route path="/roadmap" element={<Navigate to="/dashboard/idee" replace />} />
      <Route path="/sopralluogo" element={<Navigate to="/dashboard/sopralluogo" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <PackJobsProvider>
            <BgJobsProvider>
            <BatchEnhanceTrackerProvider>
            <NativeBootstrap />
            <AppRouter />
            <AuthedHolidayPopup />
            {/* iter v29 r263 — Toast iOS safe-area gestita via CSS in
                index.css (`[data-sonner-toaster][data-y-position='top']`).
                Il prop offset di sonner non era applicato in modo affidabile
                nella WebView Capacitor: il toast "Bentornato" e gli errori
                login Google finivano sopra orologio/batteria su Dynamic
                Island. Ora un CSS !important li tiene sempre sotto la
                status bar. */}
            <Toaster
              position="top-right"
              richColors
            />
            <CookieBanner />
            <PackJobsTray />
            <BgJobsTray />
            <BatchTrayPill />
            <BatchCompletionToaster />
            <GlobalMicFab />
            <CreditExhaustedModal />
            <RateLimitToastListener />
            {/* PWAInstallPrompt disabled — too intrusive on first visit. Re-enable when we have proper trigger logic. */}
            </BatchEnhanceTrackerProvider>
            </BgJobsProvider>
          </PackJobsProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
